import { addDays } from 'date-fns'
import { useMemo, useState } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'

import { COVERAGE_ICONS } from '@/components/quotes/summary/coverageIcons'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { DateInput } from '@/components/ui/DateInput'
import { Field, FieldError, FieldLegend, FieldSet } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { FillFieldKey, useFieldCheck } from '@/models/fillApplication'
import {
  COVERAGE_START_HINT,
  ENABLED_COVERAGES,
  type EnabledCoverage,
  MAX_COVERAGE_START_DAYS,
} from '@/models/insuranceCoverage'
import type { CoverageNeedsValues } from './useCoverageNeedsForm'

interface CoverageNeedsFieldsProps {
  form: UseFormReturn<CoverageNeedsValues>
  idPrefix?: string
  /** The chat's coverage step size, for a full-width page rather than a dialog. */
  largeIcons?: boolean
  /** For a dialog: no coverage illustrations, no hint under the date, and a date box sized to a date. */
  compact?: boolean
  className?: string
  /** The coverages this business can choose; trucking's when not given. */
  menu?: ReadonlyArray<EnabledCoverage>
}

export function CoverageNeedsFields({
  form,
  idPrefix = 'cn',
  largeIcons = false,
  compact = false,
  className,
  menu = ENABLED_COVERAGES,
}: CoverageNeedsFieldsProps) {
  const today = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    return start
  }, [])
  const hidden = useMemo(
    () => [{ before: today }, { after: addDays(today, MAX_COVERAGE_START_DAYS) }],
    [today]
  )
  // Shown at the field while it stands, as the field's own errors are.
  const pageStartDateCheck = useFieldCheck(FillFieldKey.START_DATE)
  // Once they're working in the field the page's note has done its job; only the field's own validation speaks
  // after. Focus alone doesn't count: a jump focuses the field to point at that note.
  const [startDateTouched, setStartDateTouched] = useState(false)
  const startDateCheck = startDateTouched ? undefined : pageStartDateCheck
  const coveragesCheck = useFieldCheck(FillFieldKey.COVERAGES)

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <Controller
        name="startDate"
        control={form.control}
        render={({ field, fieldState }) => (
          <Field
            data-fill-field={FillFieldKey.START_DATE}
            onPointerDown={() => setStartDateTouched(true)}
            onKeyDown={() => setStartDateTouched(true)}
          >
            <Label htmlFor={`${idPrefix}-start-date`}>Coverage start date</Label>
            <DateInput
              value={field.value ?? undefined}
              onChange={(date) => field.onChange(date ?? null)}
              hidden={hidden}
              defaultMonth={field.value ?? today}
              errorMessage={`Start date must be within the next ${MAX_COVERAGE_START_DAYS} days`}
              description={compact ? undefined : COVERAGE_START_HINT}
              inputGroupClassName={compact ? 'max-w-56' : undefined}
              inputProps={{
                id: `${idPrefix}-start-date`,
                'aria-invalid': !!(fieldState.error || startDateCheck) || undefined,
              }}
            />
            <FieldError>{fieldState.error?.message ?? startDateCheck}</FieldError>
          </Field>
        )}
      />
      <Controller
        name="coverages"
        control={form.control}
        render={({ field, fieldState }) => (
          <FieldSet className="gap-2" data-fill-field={FillFieldKey.COVERAGES}>
            <FieldLegend variant="label">Requested coverage(s)</FieldLegend>
            {menu.map((coverage) => {
              const Icon = COVERAGE_ICONS[coverage.coverageType]
              return (
                <CardCheckbox
                  key={coverage.coverageType}
                  isChecked={field.value.includes(coverage.coverageType)}
                  disabled={coverage.required}
                  className="py-4"
                  contentClassName="items-start"
                  onChange={(checked) =>
                    field.onChange(
                      checked
                        ? [...field.value, coverage.coverageType]
                        : field.value.filter((type) => type !== coverage.coverageType)
                    )
                  }
                >
                  <span className={cn('flex gap-3', largeIcons ? 'items-center' : 'items-start')}>
                    {!compact && (
                      <Icon className={cn('shrink-0', largeIcons ? 'size-15' : 'mt-0.5 size-5')} />
                    )}
                    <span className="flex flex-col gap-1 text-left">
                      <span className="text-foreground font-bold">
                        {coverage.label}
                        {coverage.required && (
                          <span className="text-ink-400 ml-2 text-xs font-normal">Required</span>
                        )}
                      </span>
                      <span className="text-ink-400 text-sm font-normal">{coverage.description}</span>
                    </span>
                  </span>
                </CardCheckbox>
              )
            })}
            <FieldError>{fieldState.error?.message ?? coveragesCheck}</FieldError>
          </FieldSet>
        )}
      />
    </div>
  )
}
